import React, { useMemo } from 'react';
import { Download2Icon, DownloadExcelIcon } from 'assets/icons';
import { Globals } from 'utils';
import moment from 'moment';
import { Pagination, Input, DatePicker, Button } from 'components';
import { ReportsService } from 'services';

type IonChangeEvent = React.ChangeEvent<HTMLInputElement>;

const INITIAL_STATE = {
  search: '',
  buy_date: null,
  experition_date: null
}

class ReportPlans extends React.Component {

  debounce?: NodeJS.Timeout;

  state = {
    form: INITIAL_STATE,
    data: [],
    pagination: {
      pages: 1,
      page: 1,
    },
  }

  componentDidMount(): void {
    this.load()
  }

  load = async (page: number = 1, download: boolean = false, paymentId?: number) => {
    try {
      const { buy_date, experition_date } = this.state.form;
      const _buy_date = buy_date ? moment(buy_date).format('YYYY-MM-DD') : '';
      const _experition_date = experition_date ? moment(experition_date).format('YYYY-MM-DD') : '';

      let payload = { page, ...this.state.form, buy_date: _buy_date, experition_date: _experition_date, download } as any;
      if (paymentId) payload = { ...payload, id: paymentId };

      const res = await ReportsService.plansSummary(payload)

      if (download) return res;

      const { data, ...rest } = res as any;
      this.setState({ data, pagination: rest });
    } catch (error) {
      console.log(error);
      const err = error as any;
      Globals.showError(err?.response?.data?.error ?? 'Ocurrió un error inesperado');
    }
  }

  download = async (paymentId?: number) => {
    try {
      const res: any = await this.load(this.state.pagination.page, true, paymentId);
      if (!res?.url) return;

      const link = document.createElement('a');
      link.href = Globals.fromStorage(res?.url);
      link.download = 'reporte.xlsx';
      link.target = '_blank';
      link.click();

    } catch (error) {
      Globals.showError('Ocurrió un error inesperado');
    }
  }

  change = async (value: string | number | boolean, target: keyof typeof INITIAL_STATE) => {
    const form = Globals.clone(this.state.form);
    form[target] = value;
    form['buy_date'] = form.buy_date ? new Date(form.buy_date) : null;
    form['experition_date'] = form.experition_date ? new Date(form.experition_date) : null;
    await this.setState({ form });

    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(async () => this.load(1), 800);
  }


  cleanFilters = async () => {
    await this.setState({ form: { ...INITIAL_STATE } });
    this.load(1);
  }


  render() {
    const { form } = this.state
    const canCleanFilters = JSON.stringify(this.state.form) !== JSON.stringify(INITIAL_STATE);

    return (
      <div id="report-plans">
        <section className="filters">
          <Input
            className="main-filter"
            label="Buscar"
            placeholder="Nombre del plan, empresa"
            value={form.search}
            onChange={(e: IonChangeEvent) => this.change(e.target.value, 'search')}
          />


          <DatePicker
            label="Fecha de compra"
            placeholder="Fecha de compra"
            maxDate={moment().toDate()}
            onChange={(v: string) => this.change(v, 'buy_date')}
            value={form.buy_date}
          />
          <DatePicker
            label="Fecha de vencimiento"
            placeholder="Fecha de vencimiento"
            onChange={(v: string) => this.change(v, 'experition_date')}
            value={form.experition_date}
          />
          {canCleanFilters && (
            <Button clear onClick={this.cleanFilters}>
              Limpiar
            </Button>
          )}
        </section>

        <div className="rg-table">
          <div className="rg-table-header">
            <p></p>
            <div className="rg-table-actions">
              <button onClick={() => this.download()}>
                <img src={Download2Icon} alt="Descargar" />
                <span>Descargar Excel</span>
              </button>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Nombre del plan</th>
                <th>Empresa compradora</th>
                <th>Costo del plan ($)</th>
                <th>Consultas utilizadas</th>
                <th>Consultas por utilizar</th>
                <th>Fecha de compra</th>
                <th>Método de pago</th>
                <th>Plazo de pago</th>
                <th>N de cuota</th>
                <th>Monto de la cuota</th>
                <th>Banco emisor</th>
                <th>Vence</th>
                <th>Referencia</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {this.state.data.map((planPayment: any, index: number) => {
                return <PlanItem key={planPayment?.id} planPayment={planPayment} download={this.download} />
              })}
            </tbody>
          </table>

          {!this.state.data.length && (
            <div className='no-items'>
              <p>No se encontraron resultados</p>
            </div>
          )}
        </div>

        <Pagination
          pages={this.state.pagination.pages}
          active={this.state.pagination.page}
          onChange={this.load}
        />
      </div>
    )
  }
}


const PlanItem = ({ planPayment, download }: any) => {
  const { used, available } = useMemo(() => {
    let t = 0;  // Total
    let u = 0;  // Usado
    let a = 0;  // Disponible

    planPayment?.user_plan?.specialties?.forEach((i: any) => {
      t += i.uses_quantity;
      a += i.uses_available;
    });
    u = t - a;

    return { total: t, used: u, available: a };
  }, [planPayment]);

  return (
    <tr>
      <td>{planPayment?.plan?.name}</td>
      <td>{planPayment?.user?.company_name}</td>
      <td>{Globals.formatMiles(planPayment?.user_plan?.price_annual)}</td>
      <td>{used}</td>
      <td>{available}</td>
      <td>{moment(planPayment?.date).format('DD-MM-YYYY')}</td>
      <td>{planPayment?.payment_method?.name}</td>
      <td>{planPayment?.user_plan?.period_text}</td>
      <td>{planPayment?.payment_number} de {planPayment?.user_plan?.fees}</td>
      <td>{Globals.formatMiles(planPayment?.amount, true, '$')}</td>
      <td>{planPayment?.bank?.name}</td>
      <td>{moment(planPayment?.user_plan?.expired_at).format('DD-MM-YYYY')}</td>
      <td>{planPayment?.reference}</td>
      <td className='actions'>
        <div>
          <img
            className='pointer'
            src={DownloadExcelIcon}
            alt="Descargar"
            title="Descargar"
            onClick={() => download(planPayment?.id)}
          />
        </div>
      </td>
    </tr>
  )
}

export default ReportPlans;